import { useState, useEffect, useCallback } from "react";
import { useLocation } from "wouter";

interface User {
  id: string;
  email: string;
  firstName: string;
  lastName: string;
  role: string;
  plan?: string;
  isSuperAdmin?: boolean;
  companyId?: string;
  companyName?: string;
  companyIsActive?: boolean;
  mustChangePassword?: boolean;
  hasCpf?: boolean;
  cpfMasked?: string | null;
}

interface AuthState {
  user: User | null;
  token: string | null;
  isLoading: boolean;
  isAuthenticated: boolean;
}

export function useAuth() {
  const [, setLocation] = useLocation();
  const [authState, setAuthState] = useState<AuthState>({
    user: null,
    token: null,
    isLoading: true,
    isAuthenticated: false,
  });

  // Load auth state from localStorage on mount
  useEffect(() => {
    const token = localStorage.getItem("token") || localStorage.getItem("authToken");
    const userStr = localStorage.getItem("user");
    
    if (token && userStr) {
      try {
        const user = JSON.parse(userStr);
        setAuthState({
          user,
          token,
          isLoading: false,
          isAuthenticated: true,
        });
      } catch {
        // Invalid user data, clear storage
        localStorage.removeItem("token");
        localStorage.removeItem("authToken");
        localStorage.removeItem("user");
        setAuthState({
          user: null,
          token: null,
          isLoading: false,
          isAuthenticated: false,
        });
      }
    } else {
      setAuthState({
        user: null,
        token: null,
        isLoading: false,
        isAuthenticated: false,
      });
    }
  }, []);

  const login = useCallback((token: string, user: User) => {
    localStorage.setItem("token", token);
    localStorage.setItem("authToken", token);
    localStorage.setItem("user", JSON.stringify(user));
    setAuthState({
      user,
      token,
      isLoading: false,
      isAuthenticated: true,
    });
  }, []);

  const logout = useCallback(() => {
    localStorage.removeItem("token");
    localStorage.removeItem("authToken");
    localStorage.removeItem("user");
    setAuthState({
      user: null,
      token: null,
      isLoading: false,
      isAuthenticated: false,
    });
    setLocation("/login");
  }, [setLocation]);

  const updateUser = useCallback((updates: Partial<User>) => {
    setAuthState(prev => {
      if (!prev.user) return prev;
      const updatedUser = { ...prev.user, ...updates };
      localStorage.setItem("user", JSON.stringify(updatedUser));
      return { ...prev, user: updatedUser };
    });
  }, []);

  const isAdmin = authState.user?.role === "admin" || authState.user?.role === "owner";
  const isOwner = authState.user?.role === "owner";
  const isCompanyMember = !!authState.user?.companyId;
  const mustChangePassword = authState.user?.mustChangePassword === true;

  return {
    ...authState,
    login,
    logout,
    updateUser,
    isAdmin,
    isOwner,
    isCompanyMember,
    mustChangePassword,
  };
}
